import Text from "@/components/Text";
import { FC } from "react";
import styled, { css } from "styled-components";
import { Control, useFieldArray } from "react-hook-form";

import { FormDataT } from "./EventsModal";
import FormField from "@/components/Forms/FormField";
import { ReactComponent as PlusIcon } from "@/assets/icons/violet-plus-circle.svg";
import { FormLayout } from "@/components/Forms";

const Box = styled.div<{ $withoutBottomBorder: boolean }>`
  margin: 32px 0;
  border-bottom: solid 1px ${({ theme }) => theme.colors.lightGray};
  padding-bottom: 24px;
  &:last-child {
    border-bottom: 0;
    margin-bottom: 0;
    padding-bottom: 0;
  }
  ${({ $withoutBottomBorder }) =>
    $withoutBottomBorder &&
    css`
      border-bottom: 0;
      padding-bottom: 0;
    `}
`;

const FieldHolder = styled.div`
  position: relative;
  grid-column: span 2;
`;

const AddButton = styled(Text)`
  margin-top: 16px;
  display: flex;
  align-items: center;
  transition: opacity ease-out 0.2s;
  svg {
    margin-right: 8px;
  }
  &:hover {
    opacity: 0.8;
  }
`;

const RemoveButton = styled(Text)`
  position: absolute;
  right: 0;
  top: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const Heading = styled(Text)`
  margin-bottom: 24px;
`;

interface Props {
  heading:
    | "Director"
    | "Producer"
    | "Scriptwriting"
    | "Research"
    | "Props"
    | "Art"
    | "Art Lead"
    | "Post"
    | "Video"
    | "Costumes"
    | "Thumb"
    | "Make Up"
    | "Filming Coordination";
  label: string;
  control: Control<FormDataT>;
}

const EventsUserTypeEmailBlock: FC<Props> = ({ control, heading, label }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `participants.${heading}`,
  });

  return (
    <>
      <Box $withoutBottomBorder={heading === "Art"}>
        {heading !== "Art Lead" && <Heading $type="button">{heading}</Heading>}
        <FormLayout>
          {fields.map((_, index) => (
            <FieldHolder key={index}>
              {index > 0 && (
                <RemoveButton
                  type="button"
                  $type="smallHeadline"
                  as="button"
                  $color="error"
                  onClick={() => {
                    remove(index);
                  }}
                >
                  Remove
                </RemoveButton>
              )}
              <FormField<FormDataT>
                name={`participants.${heading}.${index}.email`}
                control={control}
                label={`${label} Email`}
                labelCounter={fields.length > 1 ? index + 1 : 0}
                placeholder="Add email"
                suggestionsEndpoint="/v1/users-emails"
              />
            </FieldHolder>
          ))}
        </FormLayout>
        <AddButton
          type="button"
          $type="smallHeadline"
          as="button"
          onClick={() => append({ email: "" })}
        >
          <PlusIcon />
          Add additional {label.toLowerCase()}
        </AddButton>
      </Box>
    </>
  );
};

export default EventsUserTypeEmailBlock;
